import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Edit, Plus, Filter, Trash2, Edit2, Search, 
  Tag, Hash, Key, ScanLine, Shield, CircleDot, 
  Layers, Package, BadgeDollarSign, Clock, Image as ImageIcon,
  ArrowRightLeft, Pencil, X, ChevronLeft, ChevronRight, Loader
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { masterService } from "../../api/masterService";
import { adminService } from "../../api/adminService";
import { userService } from "../../api/userService";
import conversionService from "../../api/conversionService";
import { useNotification } from "../../contexts/NotificationContext";

import EditProductModal from "./components/EditProductModal";
import LinkAddonGroupModal from "./components/LinkAddonGroupModal";
import EditVariantModal from "./components/EditVariantModal";
import EditInfoLainModal from "./components/EditInfoLainModal";
import ProductInfoLain from "./components/ProductInfoLain";
import ProductAuditLog from "./components/ProductAuditLog";
import ProductSellingPricesSection from "./components/ProductSellingPricesSection";
import AddBrandVariantsModal from "../../components/AddBrandVariantsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ImageCropperModal from "../../components/ImageCropperModal";
import ImageSourcePickerModal from "../../components/ImageSourcePickerModal";
import { getImageUrl } from "../../utils/imageUtils";


export default function ProductDetailWeb() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [conversionRules, setConversionRules] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Modal States
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [isEditInfoLainOpen, setIsEditInfoLainOpen] = useState(false);
  const [isEditVariantOpen, setIsEditVariantOpen] = useState(false);
  const [isEditingBrandVariant, setIsEditingBrandVariant] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // New modal states from old design
  const [isAddBrandVariantOpen, setIsAddBrandVariantOpen] = useState(false);
  const [isLinkAddonGroupOpen, setIsLinkAddonGroupOpen] = useState(false);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: () => {},
  });

  // Filter States
  const [auditFilter, setAuditFilter] = useState({ start_date: '', end_date: '', user_id: '' });
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [usersList, setUsersList] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const [cropModal, setCropModal] = useState({
    isOpen: false,
    imageSrc: null,
    targetId: null,
    isPrimary: false
  });

  const [pickerModal, setPickerModal] = useState({
    isOpen: false,
    targetId: null,
    isPrimary: false
  });

  const triggerImagePicker = (targetId = null, isPrimary = false) => {
    setPickerModal({
      isOpen: true,
      targetId,
      isPrimary
    });
  };

  const handleSourceSelect = async (source) => {
    const { targetId, isPrimary } = pickerModal;

    if (source === "gallery") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/jpeg,image/png";
      input.onchange = (e) => handleImageSelect(e, targetId, isPrimary);
      input.click();
    } else if (source === "camera") {
      try {
        const { Camera, CameraResultType } = await import("@capacitor/camera");
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl
        });

        if (image && image.dataUrl) {
          setCropModal({
            isOpen: true,
            imageSrc: image.dataUrl,
            targetId,
            isPrimary
          });
        }
      } catch (err) {
        console.error("Camera capture error:", err);
        if (err.message && !err.message.includes("cancelled") && !err.message.includes("cancel")) {
          showError("Gagal membuka kamera. Pastikan izin kamera diberikan.");
        }
      }
    }
  };

  const handleImageSelect = (e, targetId = null, isPrimary = false) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showError("Ukuran gambar maksimal 5MB");
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setCropModal({
        isOpen: true,
        imageSrc: reader.result,
        targetId,
        isPrimary
      });
    };
    e.target.value = '';
  };

  const handleCropSave = async (base64Image) => {
    try {
      if (cropModal.targetId) {
        await masterService.products.deleteImage(id, cropModal.targetId);
      }
      await masterService.products.addImage(id, {
        image_base64: base64Image,
        sort_order: (product.images?.length || 0) + 1,
        is_primary: product.images?.length === 0 || cropModal.isPrimary
      });
      showSuccess("Gambar berhasil disimpan");
      fetchData();
    } catch (err) {
      showError(err.message || "Gagal menyimpan gambar");
    }
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      const [productRes, variantsRes, rulesRes] = await Promise.all([
        masterService.products.get(id),
        masterService.products.variants.list(id),
        conversionService.rules.list({ product_id: id, limit: 100 }).catch(() => ({ data: { items: [] } }))
      ]);
      setProduct(productRes.data);
      setVariants(variantsRes.data || []);
      const rulesData = rulesRes?.data?.items || rulesRes?.data || rulesRes || [];
      setConversionRules(Array.isArray(rulesData) ? rulesData : []);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      showError("Gagal memuat data produk");
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await masterService.products.getAuditLogs(id, {
        page: auditPage,
        limit: 10,
        start_date: auditFilter.start_date,
        end_date: auditFilter.end_date,
        user_id: auditFilter.user_id
      });
      setAuditLogs(res.data || []);
      if (res.meta) {
        setAuditTotalPages(Math.ceil(res.meta.total / res.meta.limit));
      }
    } catch (err) {
      console.error("Error fetching audit logs", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await userService.listTenantUsers({ limit: 100 });
      setUsersList(res.data?.items || res.data || []);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [id, auditPage, auditFilter]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchData();
  }, [id]);

  const sidebarToggle = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  const handleDeleteVariant = (variant, isBrandVariant = false) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Varian?",
      message: `Apakah Anda yakin ingin menghapus varian "${isBrandVariant ? (variant.brand_name || variant.name) : variant.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.products.variants.delete(id, variant.id);
          showSuccess("Varian berhasil dihapus");
          fetchData();
        } catch (err) {
          showError(err.message || "Gagal menghapus varian");
        } finally {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };
  const handleLinkAddonGroup = () => {
    setIsLinkAddonGroupOpen(true);
  };

  const handleUnlinkAddonGroupConfirm = (group) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Kaitan Grup?",
      message: `Apakah Anda yakin ingin menghapus kaitan grup pilihan "${group.name}" dari produk ini?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          const newGroupIds = (product.addon_groups || [])
            .filter((g) => g.id !== group.id)
            .map((g) => g.id);
          await masterService.addonGroups.setProductAddonGroups(id, newGroupIds);
          showSuccess("Kaitan grup pilihan extra berhasil dihapus");
          fetchData();
        } catch (error) {
          showError("Gagal menghapus kaitan grup pilihan extra");
        } finally {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#f8fafc]">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
        <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
          <Header />
          <div className="p-8 flex items-center justify-center min-h-[60vh] text-slate-500">
            <Loader className="w-6 h-6 animate-spin mr-3 text-teal-600" />
            <p className="font-medium text-lg">Memuat detail produk...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex h-screen bg-[#f8fafc]">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
        <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
          <Header />
          <div className="p-8 flex items-center justify-center min-h-[60vh]">
            <p>Produk tidak ditemukan</p>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (price) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('id-ID', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Image Slot Logic — fallback to legacy image_url if images array is empty
  const rawImages = product.images && product.images.length > 0
    ? product.images
    : (product.image_url ? [{ id: null, image_url: product.image_url, is_primary: true, sort_order: 1 }] : []);
  const imagesArr = rawImages.slice().sort((a, b) => {
    if (a.is_primary) return -1;
    if (b.is_primary) return 1;
    return (a.sort_order || 99) - (b.sort_order || 99);
  });
  const mainImage = imagesArr[0];
  const thumbImages = imagesArr.slice(1, 5);

  const marginProduk = (product.sell_price || 0) - (product.purchase_price || 0);
  const baseUnitId = product.unit_id;
  const baseUnitName = product.unit_name || "Pcs";

  // Split variants based on user requirements
  const productVariants = variants.filter(v => v.brand_id == null);
  const brandVariants = variants.filter(v => v.brand_id != null);

  const brandsList = brandVariants.length > 0 
    ? [...new Set(brandVariants.map(v => v.brand_name).filter(Boolean))].join(", ") 
    : "-";

  // Derived data: group variants by brand for the Varian Merek table
  const groupedBrandVariants = (() => {
    const map = new Map();
    brandVariants.forEach((v) => {
      const key = v.brand_id;
      if (!map.has(key)) {
        map.set(key, {
          brand_id: v.brand_id,
          brand_name: v.brand_name || "-",
          status: v.status,
          rows: [],
        });
      }
      const group = map.get(key);
      group.rows.push(v);
      if (v.status === "ACTIVE") group.status = "ACTIVE";
    });
    return Array.from(map.values());
  })();

  const getHargaDasar = (group, productUnitId, type = 'sell_price') => {
    const base = group.rows.find((r) => r.unit_id === productUnitId);
    if (base && Number(base[type]) > 0) return base[type];
    const prices = group.rows
      .map((r) => Number(r[type] || 0))
      .filter((p) => p > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

let filteredAuditLogs = auditLogs || [];

  // Dynamic Pricing & Stock Calculation
  let displayPurchasePrice = product?.purchase_price || 0;
  let displaySellPrice = product?.sell_price || 0;
  if ((!product?.sell_price || product.sell_price === 0) && variants.length > 0) {
    displayPurchasePrice = variants[0].purchase_price || 0;
    displaySellPrice = variants[0].sell_price || 0;
  }

  const baseStock = product?.stok || 0;
  const variantsStock = variants.reduce((sum, v) => sum + (v.stok || 0), 0);
  // Jika ada varian: total stok dari varian. Jika tidak ada varian: pakai stok default produk (dari inventory)
  const calculatedTotalStock = variants.length > 0 ? variantsStock : baseStock;

  return (
    <div className="flex h-screen bg-[#f8fafc]">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
      <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
        <Header />

        <div className="p-6 lg:p-8 w-full mx-auto space-y-6 pb-16">
          
          {/* Header Section */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div className="flex items-start gap-4 min-w-0">
              <button 
                onClick={() => navigate("/master-data/produk")}
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 shrink-0 mt-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-[22px] font-bold text-gray-900 tracking-tight">Detail Produk</h1>
                <div className="flex items-center text-sm font-medium text-slate-400 mt-1">
                  <span className="hover:text-teal-600 cursor-pointer transition-colors shrink-0" onClick={() => navigate("/master-data/produk")}>Data Produk</span>
                  <ChevronRight className="w-4 h-4 mx-1.5 text-slate-300 shrink-0" />
                  <span className="text-slate-600 font-semibold truncate">{product.name}</span>
                </div>
              </div>
            </div>
            <button onClick={() => setIsEditProductOpen(true)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-teal-500 rounded-xl text-teal-600 font-semibold hover:bg-teal-50 shadow-sm transition-colors w-full sm:w-auto shrink-0">
              <Edit className="w-4 h-4" />
              Edit Produk
            </button>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden flex flex-col md:flex-row p-6 lg:p-8 gap-8 lg:gap-12">
            {/* Left: Images */}
            <div className="w-full md:w-[350px] shrink-0 flex flex-col gap-4">
              <div className="aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 relative group flex items-center justify-center shadow-inner">
                {mainImage ? (
                  <>
                    <img src={getImageUrl(mainImage.image_url)} className="w-full h-full object-cover" alt="Product" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x300?text=Error'; }} />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 backdrop-blur-[2px]">
                      <button 
                        onClick={() => triggerImagePicker(mainImage.id, true)} 
                        className="px-4 py-2 bg-white rounded-lg font-bold text-sm text-slate-800 cursor-pointer w-28 text-center hover:bg-slate-50 transition-colors"
                      >
                        Ubah
                      </button>
                      {mainImage.id && (
                        <button 
                          onClick={() => masterService.products.deleteImage(id, mainImage.id).then(() => { showSuccess("Gambar dihapus"); fetchData(); })}
                          className="px-4 py-2 bg-rose-500 rounded-lg font-bold text-sm text-white cursor-pointer w-28 text-center hover:bg-rose-600 transition-colors">
                          Hapus
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <button 
                    onClick={() => triggerImagePicker(null, true)} 
                    className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-teal-500 hover:bg-teal-50 transition-colors"
                  >
                    <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                    <span className="text-sm font-semibold text-center leading-tight">Tambah Utama<br/><span className="text-[10px] font-normal text-slate-400">JPG/PNG (Maks 5MB)</span></span>
                  </button>
                )}
              </div>

              {/* HIDDEN FOR NOW: Single product photo requirement (kept for future use) */}
              {/* 
              <div className="grid grid-cols-4 gap-3">
                {thumbImages.map((img, idx) => (
                  <div key={img.id} className="aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden border border-slate-200 relative group shadow-sm transition-all hover:border-teal-300">
                    <img src={getImageUrl(img.image_url)} className="w-full h-full object-cover" alt={`Thumb ${idx + 1}`} onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100?text=Error'; }} />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1.5 transition-opacity backdrop-blur-[1px]">
                      <button 
                        onClick={() => triggerImagePicker(img.id, false)} 
                        className="px-2 py-1 bg-white rounded-md font-semibold text-[10px] text-slate-800 cursor-pointer w-[80%] text-center hover:bg-slate-50 transition-colors shadow-sm"
                      >
                        Ubah
                      </button>
                      <button 
                        onClick={() => masterService.products.deleteImage(id, img.id).then(() => { showSuccess("Gambar dihapus"); fetchData(); })}
                        className="px-2 py-1 bg-rose-500 rounded-md font-semibold text-[10px] text-white cursor-pointer w-[80%] text-center hover:bg-rose-600 transition-colors shadow-sm">
                        Hapus
                      </button>
                    </div>
                  </div>
                ))}
                {imagesArr.length < 5 && (
                  <button 
                    onClick={() => triggerImagePicker(null, false)} 
                    className="aspect-[4/3] border border-dashed border-teal-300 text-teal-500 rounded-xl flex flex-col items-center justify-center hover:bg-teal-50 transition-colors relative overflow-hidden group cursor-pointer shadow-sm"
                  >
                    <Plus className="w-5 h-5 mb-0.5 group-hover:scale-110 transition-transform" strokeWidth={2.5} />
                    <span className="text-[10px] font-semibold text-center leading-tight">Tambah<br/><span className="text-[8px] font-normal text-slate-400">Maks 5MB</span></span>
                  </button>
                )}
              </div>
              */}
            </div>

            {/* Right: Info */}
            <div className="w-full flex-1">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-[24px] lg:text-[28px] font-extrabold text-gray-900 uppercase tracking-wide">
                  {product.name}
                </h2>
                {product.status === "ACTIVE" ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold tracking-wide">Aktif</span>
                ) : (
                  <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-xs font-bold tracking-wide">Non-Aktif</span>
                )}
              </div>
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-y-6 gap-x-12 text-[15px]">
                {/* Column 1 */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><Tag className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Kategori</span>
                    <span className="text-gray-900 font-bold truncate">{product.category_name || "-"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><ScanLine className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Barcode</span>
                    <span className="text-gray-900 font-bold truncate">{product.barcode || "-"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><Key className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">SKU</span>
                    <span className="text-gray-900 font-bold truncate">{product.sku || "-"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><Layers className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Stok</span>
                    <span className="text-gray-900 font-bold">{calculatedTotalStock}</span>
                  </div>
                </div>

                {/* Column 2 */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><Package className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-36 shrink-0">Satuan</span>
                    <span className="text-gray-900 font-bold">{product.unit_name || "-"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><BadgeDollarSign className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-36 shrink-0">Harga Modal</span>
                    <span className="text-gray-900 font-bold">{formatCurrency(displayPurchasePrice)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-500 shrink-0"><Tag className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-36 shrink-0">Harga Jual</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-bold">{formatCurrency(displaySellPrice)}</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 flex justify-center text-teal-500 mt-0.5 shrink-0"><Clock className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-36 shrink-0">Update Terakhir</span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-gray-900 font-bold">{formatDate(product.updated_at)}</span>
                      <span className="text-teal-600 text-sm">{product.updated_by || "Sistem"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* HIDDEN TEMPORARILY: Deskripsi Produk
              <hr className="my-8 border-slate-100" />
              
              <div>
                <h3 className="text-teal-600 font-medium mb-3">Deskripsi Produk</h3>
                <p className="text-gray-700 text-sm leading-relaxed font-medium">
                  {product.description || "-"}
                </p>
              </div>
              */}
            </div>
          </div>



          {/* Varian Harga Jual (Khusus Mode Retail) */}
          <ProductSellingPricesSection
            product={product}
            onRefresh={fetchData}
          />

          {/* Varian Produk / Merek (Global Varian) */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
              <h3 className="text-gray-900 text-[18px] font-bold">Varian Produk / Merek</h3>
              <button onClick={() => { setSelectedVariant(null); setIsEditingBrandVariant(false); setIsEditVariantOpen(true); }} className="flex items-center justify-center gap-2 px-5 py-2 bg-white border border-teal-500 text-teal-600 rounded-xl hover:bg-teal-50 font-semibold text-sm transition-colors shadow-sm w-full sm:w-auto">
                <Plus className="w-4 h-4" strokeWidth={2.5} /> Tambah Varian
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">No</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Nama Varian</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Barcode</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Modal</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Jual</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Satuan</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Stok</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center w-24">Aksi</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 font-semibold">
                  {variants.length > 0 ? (
                    variants.map((variant, idx) => {
                      const vMargin = (variant.sell_price || 0) - (variant.purchase_price || 0);
                      const variantDisplayName = variant.brand_name || variant.name || "-";
                      return (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-4 text-center text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-4">{variantDisplayName}</td>
                        <td className="px-4 py-4 text-slate-600 font-mono text-sm">{variant.sku || "-"}</td>
                        <td className="px-4 py-4 text-slate-600 font-mono text-sm">{variant.barcode || "-"}</td>
                        <td className="px-4 py-4">{formatCurrency(variant.purchase_price)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-teal-600">{formatCurrency(variant.sell_price)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">{variant.unit_name || product?.unit_name || "-"}</td>
                        <td className="px-4 py-4 font-semibold text-gray-900">{variant.stok || "0"}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedVariant(variant); setIsEditingBrandVariant(!!variant.brand_id); setIsEditVariantOpen(true); }} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-md transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteVariant(variant, false)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )})
                  ) : (
                    <tr className="border-b border-slate-50">
                      <td colSpan="7" className="py-8 text-center">
                         <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
                           <Layers className="w-6 h-6 text-slate-400" />
                         </div>
                         <p className="text-slate-500 font-medium">Belum ada varian produk / merek</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Informasi Lainnya (Retail Mode Only) */}
          {!localStorage.getItem("PREVIEW_APPS")?.includes("true") && (
            <ProductInfoLain 
              product={product} 
              onEdit={() => setIsEditInfoLainOpen(true)} 
            />
          )}

          <ProductAuditLog productId={id} />

        </div>
      </div>

      <EditInfoLainModal
        isOpen={isEditInfoLainOpen}
        onClose={() => setIsEditInfoLainOpen(false)}
        product={product}
        onSuccess={fetchData}
      />

      {/* HIDDEN TEMPORARILY: AddBrandVariantsModal (Dua modal varian disatukan)
      <AddBrandVariantsModal
        isOpen={isAddBrandVariantOpen}
        onClose={() => setIsAddBrandVariantOpen(false)}
        productId={id}
        product={product}
        baseUnitId={product?.unit_id}
        baseUnitName={product?.unit_name}
        onSuccess={fetchData}
      />
      */}
      
      <EditVariantModal
        isOpen={isEditVariantOpen}
        onClose={() => { setIsEditVariantOpen(false); setSelectedVariant(null); }}
        variant={selectedVariant}
        productId={id}
        product={product}
        onSuccess={fetchData}
        isBrandVariant={isEditingBrandVariant}
        isRetail={true}
      />
      
      <EditProductModal
        isOpen={isEditProductOpen}
        onClose={() => setIsEditProductOpen(false)}
        product={product}
        onSuccess={fetchData}
      />

      <LinkAddonGroupModal
        isOpen={isLinkAddonGroupOpen}
        onClose={() => setIsLinkAddonGroupOpen(false)}
        productId={id}
        linkedGroupIds={(product.addon_groups || []).map((g) => g.id)}
        onSuccess={fetchData}
      />

      
      <ImageCropperModal
        isOpen={cropModal.isOpen}
        imageSrc={cropModal.imageSrc}
        onClose={() => setCropModal({ isOpen: false, imageSrc: null, targetId: null, isPrimary: false })}
        onSave={handleCropSave}
      />

      <ImageSourcePickerModal
        isOpen={pickerModal.isOpen}
        onClose={() => setPickerModal({ isOpen: false, targetId: null, isPrimary: false })}
        onSelect={handleSourceSelect}
      />

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        onConfirm={confirmationModal.onConfirm}
        isLoading={isDeleting}
        onCancel={() => setConfirmationModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
